import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Plus, Sparkles, SortAsc, Tag, Loader2, Users } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import { PersonaLibraryCard } from './PersonaLibraryCard';

export function PersonaLibrary({ library }) {
  const { t } = useTranslation();
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateDesc, setGenerateDesc] = useState('');

  const handleGenerate = async () => {
    const persona = await library.generatePersona(generateDesc);
    if (persona) {
      setShowGenerateDialog(false);
      setGenerateDesc('');
      library.openEditor(persona);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              value={library.searchQuery}
              onChange={e => library.setSearchQuery(e.target.value)}
              placeholder={t('personaLib.searchPlaceholder')}
              className="input pl-10 w-full"
            />
          </div>

          {/* Tag filter */}
          {library.allTags.length > 0 && (
            <select
              value={library.tagFilter}
              onChange={e => library.setTagFilter(e.target.value)}
              className="input"
            >
              <option value="">{t('personaLib.allTags')}</option>
              {library.allTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          )}

          {/* Sort */}
          <select
            value={library.sortBy}
            onChange={e => library.setSortBy(e.target.value)}
            className="input"
          >
            <option value="updated_at">{t('personaLib.sortRecent')}</option>
            <option value="name">{t('personaLib.sortName')}</option>
            <option value="age">{t('personaLib.sortAge')}</option>
            <option value="usage_count">{t('personaLib.sortUsage')}</option>
            <option value="created_at">{t('personaLib.sortCreated')}</option>
          </select>

          {/* Actions */}
          <button
            onClick={() => library.openEditor(null)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('personaLib.createNew')}
          </button>
          <button
            onClick={() => setShowGenerateDialog(true)}
            disabled={library.isAiWorking}
            className="btn btn-primary flex items-center gap-2"
          >
            {library.isAiWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {t('personaLib.aiGenerate')}
          </button>
        </div>
      </div>

      {/* Persona grid */}
      {library.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : library.libraryPersonas.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 shadow-sm border border-stone-200 text-center">
          <Users className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-stone-600 mb-2">{t('personaLib.emptyTitle')}</h3>
          <p className="text-stone-400 mb-6">{t('personaLib.emptyDesc')}</p>
          <div className="flex justify-center gap-3">
            <button onClick={() => library.openEditor(null)} className="btn btn-secondary flex items-center gap-2">
              <Plus className="w-4 h-4" /> {t('personaLib.createNew')}
            </button>
            <button onClick={() => library.setSubView('archetypes')} className="btn btn-primary flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> {t('personaLib.browseArchetypes')}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {library.libraryPersonas.map(persona => (
            <PersonaLibraryCard
              key={persona.id}
              persona={persona}
              onEdit={() => library.openEditor(persona)}
              onDuplicate={() => library.duplicatePersona(persona.id)}
              onDelete={() => library.deletePersona(persona.id)}
              onAddToPanel={() => library.addToPanel(persona.id)}
              isInPanel={library.currentPanel.persona_ids.includes(persona.id)}
            />
          ))}
        </div>
      )}

      {/* AI Generate Dialog */}
      {showGenerateDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowGenerateDialog(false)}>
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-serif text-xl mb-4">{t('personaLib.generateTitle')}</h3>
            <textarea
              value={generateDesc}
              onChange={e => setGenerateDesc(e.target.value)}
              placeholder={t('personaLib.generatePlaceholder')}
              rows={3}
              className="input w-full mb-4"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowGenerateDialog(false)} className="btn btn-secondary">{t('personaLib.cancel')}</button>
              <button
                onClick={handleGenerate}
                disabled={library.isAiWorking}
                className="btn btn-primary flex items-center gap-2"
              >
                {library.isAiWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {t('personaLib.generate')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
